import { useParams } from '@vkontakte/vk-mini-apps-router';
import { type NavIdProps } from '@vkontakte/vkui';
import type { ReactNode } from 'react';
import { ABILITY_KIND_LABELS, ABILITY_ORIGIN_LABELS } from '../../api/types';
import { useBack } from '../../app/useBack';
import { t } from '../../i18n';
import { ClockIcon, PassportIcon } from '../../shared/icons';
import {
  CardList,
  ErrorState,
  Gutter,
  ImageMark,
  LoadingState,
  Media,
  Muted,
  PropertyCard,
  Screen,
  Section,
} from '../../shared/ui';
import { formatDate } from '../review/staffKit';
import { useCharacterCard, type CardAbility, type CardGrantedAbility } from './characterCard';

/**
 * Разделы чужого досье: склад, питомцы, способности.
 *
 * Отдельные экраны, а не блоки на карточке: в холстах досье заканчивается
 * плитками со счётом, и каждая плитка ведёт на свой экран. Держать это всё
 * одной лентой значило бы листать чужую биографию ради того, чтобы увидеть
 * один предмет.
 *
 * Своего запроса у раздела нет: публичная ручка реестра отдаёт карточку
 * целиком, и раздел берёт из ответа свою часть.
 */

function PropertyPanel({
  id,
  title,
  count,
  unit,
  children,
}: {
  id: string | undefined;
  title: string;
  count: number | null;
  /** Ключ единицы в словаре: счёт в шапке идёт со словом и склоняется. */
  unit: string;
  children: ReactNode;
}) {
  const back = useBack();

  return (
    <Screen id={id} title={title} back={back} count={count} unit={unit}>
      {children}
    </Screen>
  );
}

/**
 * Общая обвязка: сразу карточки.
 *
 * Подписи раздела здесь нет намеренно: имя персонажа стояло над списком и
 * повторяло то, откуда игрок только что пришёл, — в шапке уже написано, что
 * это склад, а чьё досье открыто, он знает. Одна подпись на весь экран, и та
 * ничего не добавляла.
 */
function PropertySection({ empty, children }: { empty: string; children: ReactNode }) {
  const isEmpty = Array.isArray(children) && children.length === 0;

  return (
    <Section>
      {isEmpty ? (
        <Gutter>
          <Muted>{empty}</Muted>
        </Gutter>
      ) : (
        <CardList>{children}</CardList>
      )}
    </Section>
  );
}

export function CharacterItemsPanel({ id }: NavIdProps) {
  const params = useParams<'characterId'>();
  const { card, error, loading, retry } = useCharacterCard(Number(params?.characterId));

  return (
    <PropertyPanel
      id={id}
      title={t('common.section.inventory')}
      count={card ? card.items.length : null}
      unit="item"
    >
      {loading ? <LoadingState text={t('registry.loading.items')} /> : null}
      {!loading && !card ? <ErrorState message={error} onRetry={retry} /> : null}

      {card ? (
        <PropertySection empty={t('registry.empty.items')}>
          {/* Знака состояния нет: в чужом досье лежит только принятое, и
              подписывать каждую строку «в карточке» нечем. Меток тоже нет —
              они личные, публичная ручка реестра их не отдаёт. */}
          {card.items.map((item) => (
            <PropertyCard
              key={item.id}
              image={item.image}
              kind="item"
              title={item.title}
              description={item.description}
            />
          ))}
        </PropertySection>
      ) : null}
    </PropertyPanel>
  );
}

export function CharacterPetsPanel({ id }: NavIdProps) {
  const params = useParams<'characterId'>();
  const { card, error, loading, retry } = useCharacterCard(Number(params?.characterId));

  return (
    <PropertyPanel
      id={id}
      title={t('common.section.pets')}
      count={card ? card.pets.length : null}
      unit="pet"
    >
      {loading ? <LoadingState text={t('registry.loading.pets')} /> : null}
      {!loading && !card ? <ErrorState message={error} onRetry={retry} /> : null}

      {card ? (
        <PropertySection empty={t('registry.empty.pets')}>
          {card.pets.map((animal) => (
            <PropertyCard
              key={animal.id}
              image={animal.image}
              kind="pet"
              gallery={[animal.image, animal.passportImage]}
              title={animal.name}
              /* Паспорт отметкой в строке фактов: второй миниатюрой он занимал
                 столько же места, сколько само животное. */
              meta={[
                animal.species,
                animal.passportImage === null ? null : (
                  <ImageMark key="passport" image={animal.passportImage}>
                    <PassportIcon size={11} />
                    {t('registry.pet.passport')}
                  </ImageMark>
                ),
              ]}
              description={animal.description}
            />
          ))}
        </PropertySection>
      ) : null}
    </PropertyPanel>
  );
}

export function CharacterAbilitiesPanel({ id }: NavIdProps) {
  const params = useParams<'characterId'>();
  const { card, error, loading, retry } = useCharacterCard(Number(params?.characterId));
  const total = card ? card.abilities.active.length + card.abilities.archive.length : null;

  return (
    <PropertyPanel id={id} title={t('common.section.abilities')} count={total} unit="ability">
      {loading ? <LoadingState text={t('registry.loading.abilities')} /> : null}
      {!loading && !card ? <ErrorState message={error} onRetry={retry} /> : null}

      {card ? (
        <PropertySection empty={t('registry.empty.abilities')}>
          {[
            // Стартовая способность — это пункт анкеты, строки в базе за ней
            // нет, поэтому ключом служит её название.
            ...card.abilities.active.map((entry) => (
              <AbilityCard
                key={entry.origin === 'granted' ? `granted-${entry.id}` : `form-${entry.title}`}
                ability={entry}
                expired={false}
              />
            )),
            ...card.abilities.archive.map((entry) => (
              <AbilityCard key={`archive-${entry.id}`} ability={entry} expired />
            )),
          ]}
        </PropertySection>
      ) : null}
    </PropertyPanel>
  );
}

/**
 * Карточка способности. Стартовая и выданная различаются не оформлением, а
 * тем, что у стартовой нет ни срока, ни источника: строки в таблице за ней
 * нет, есть девятый пункт анкеты.
 */
function AbilityCard({ ability, expired }: { ability: CardAbility; expired: boolean }) {
  const granted = ability.origin === 'granted' ? (ability as CardGrantedAbility) : null;
  // У стартовой способности картинки не бывает вовсе — кадр всё равно стоит,
  // с заглушкой: карточка способности не должна отличаться формой от карточки
  // предмета в соседнем разделе.
  const image = granted === null ? null : granted.image;
  const tone = expired || granted === null ? 'archived' : 'published';

  return (
    <PropertyCard
      image={image}
      kind="ability"
      title={ability.title || t('registry.ability.untitled')}
      badge={
        granted === null
          ? ABILITY_ORIGIN_LABELS.form.toLocaleLowerCase('ru')
          : expired
            ? t('registry.ability.expired')
            : t('common.state.published')
      }
      tone={tone}
      meta={granted === null ? undefined : [ABILITY_KIND_LABELS[granted.kind]]}
      description={ability.description}
      extra={
        granted === null || granted.expiresAt === null ? undefined : (
          <Media aside={<ClockIcon tone={expired ? 'muted' : 'accent'} />}>
            <Muted>
              {expired
                ? t('registry.ability.expiredAt', { date: formatDate(granted.expiresAt) })
                : t('registry.ability.activeUntil', { date: formatDate(granted.expiresAt) })}
            </Muted>
          </Media>
        )
      }
    />
  );
}
